/**
 * Display helpers for coach series.
 *
 * These are presentation-only: they turn fields the coach-series endpoint
 * already returns into the strings staff read. No business rule lives here —
 * the recurrence rule, the slot math and the active/ended definition are all
 * owned by `prospin-api`.
 */
import { minutesOfDay } from './time';
import type { CoachSeries, CoachSeriesOccurrence, OccurrenceSkipReason } from './types';

/** `day_of_week` is ISO with 0 = Monday, matching the `coach_series` schema. */
export const WEEKDAY_LABELS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

/** Short form, for table columns where the full name won't fit. */
export const WEEKDAY_SHORT_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/**
 * Full weekday name for a `day_of_week`. An out-of-range value renders as
 * `Day <n>?` rather than `undefined` — an obviously-wrong label beats a
 * plausible-looking one (same rule as `formatClock` in `time.ts`).
 */
export function weekdayLabel(dayOfWeek: number): string {
  return WEEKDAY_LABELS[dayOfWeek] ?? `Day ${dayOfWeek}?`;
}

/** Short weekday name for a `day_of_week`. */
export function weekdayShortLabel(dayOfWeek: number): string {
  return WEEKDAY_SHORT_LABELS[dayOfWeek] ?? `?${dayOfWeek}`;
}

/**
 * The series recurrence rule in words. Every coach series is weekly by schema
 * (`coach_series` stores a single `day_of_week`), so the cadence is a constant,
 * not something inferred from the data.
 */
export function recurrenceLabel(dayOfWeek: number): string {
  return `Weekly · every ${weekdayLabel(dayOfWeek)}`;
}

/**
 * What kind of thing a coach-series row is, for a column header or a badge.
 *
 * `is_one_time` is derived and returned by the API (`starts_on == ends_on`,
 * givip/prospin-api#83) — never re-derived here, so the two cannot disagree
 * about a session's identity.
 */
export function seriesKindLabel(isOneTime: boolean): string {
  return isOneTime ? 'One-time' : 'Weekly';
}

/**
 * When a series happens, in words.
 *
 * A one-time session is stored as a series of length 1, so it still carries a
 * `day_of_week` — rendering "Weekly · every Monday" for a single trial lesson
 * is exactly the misread this label removes. An api older than #83 omits
 * `is_one_time`; falsy then means "recurring", which is what every series was
 * before that endpoint existed.
 */
export function scheduleLabel(
  series: Pick<CoachSeries, 'day_of_week' | 'starts_on' | 'is_one_time'>,
): string {
  if (series.is_one_time) {
    return `One-time · ${weekdayLabel(series.day_of_week)} ${series.starts_on}`;
  }
  return recurrenceLabel(series.day_of_week);
}

/**
 * Minutes between two venue-local "HH:MM" wall-clock strings.
 *
 * Parsing goes through `minutesOfDay` so this shares one time parser with the
 * rest of the app. Returns null for anything that doesn't parse, and for a
 * non-positive span — the API rejects those on create
 * (`validate_series_slot`: duration ≥ MIN_SLOT_MINUTES), so a series that
 * shows one has data we shouldn't be narrating. Callers hide the value rather
 * than render `NaN min` or `-60 min`.
 */
export function durationMinutes(startTime: string, endTime: string): number | null {
  const s = minutesOfDay(startTime);
  const e = minutesOfDay(endTime);
  if (s === null || e === null) return null;
  return e > s ? e - s : null;
}

/**
 * One-line summary of the occurrence window the detail endpoint returns.
 *
 * The endpoint returns every booking for the series in the next 31 days
 * **including cancelled ones** — ending a series cancels its future
 * occurrences rather than deleting them. A bare row count therefore reads as
 * "5 sessions still to come" on a series that has none, which is exactly the
 * cross-referencing this page exists to remove. Splitting the two counts is a
 * presentation choice over rows already in the response, not a recomputed
 * series fact; the table below shows the same statuses per row.
 */
export function occurrenceSummary(occurrences: Pick<CoachSeriesOccurrence, 'status'>[]): string {
  const cancelled = occurrences.filter((o) => o.status === 'cancelled').length;
  const scheduled = occurrences.length - cancelled;
  if (cancelled === 0) {
    return `${scheduled} occurrence${scheduled === 1 ? '' : 's'} in the next 31 days`;
  }
  return `${scheduled} scheduled · ${cancelled} cancelled — next 31 days`;
}

/**
 * Why a series-wide edit left an occurrence alone, in words.
 *
 * The vocabulary is `prospin-api`'s (`classify_occurrence` for the time shift,
 * `classify_occurrence_for_transfer` for the table transfer) — this only
 * translates it. An unrecognised reason is returned as-is rather than mapped to
 * a catch-all: a reason the API adds later must stay visible in the preview,
 * and an obviously-raw label beats a plausible-looking wrong one (same rule as
 * `weekdayLabel`).
 */
const SKIP_REASON_LABELS: Record<string, string> = {
  elapsed: 'Already started',
  cancelled: 'Cancelled',
  manually_modified: 'Edited individually',
  // Transfer-only (api#85): somebody moved this occurrence to another table
  // deliberately, and a series-level transfer does not overrule that.
  relocated: 'Moved to another table individually',
};

export function occurrenceSkipLabel(reason: OccurrenceSkipReason): string {
  return SKIP_REASON_LABELS[reason] ?? reason;
}

export type SeriesStatus = 'active' | 'ended';

/**
 * Whether a series is still running.
 *
 * This mirrors `prospin-api`'s own definition — `list_series` selects active
 * rows with `ends_on IS NULL OR ends_on >= CURRENT_DATE` — and is the one
 * series fact this client derives rather than reads. `today` must be a
 * venue-local `YYYY-MM-DD` (`todayLocal()`), never a UTC or device-zone date;
 * both arguments are plain date strings so the comparison is a lexicographic
 * one with no timezone in play.
 *
 * Tracked for removal by givip/prospin-api#106, which adds `status` to the
 * coach-series payload. Delete this once the client can read it directly.
 */
export function seriesStatus(endsOn: string | null, today: string): SeriesStatus {
  if (endsOn === null) return 'active';
  return endsOn >= today ? 'active' : 'ended';
}
